/**
 * Created by sdesai on 8/12/2022
 * File: DeleteInstanceModal
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Bold,
  Button,
  LoadingIcon,
} from '@panwds/react-ui'
import { Form, SubmitButton, FormLayout, Input } from '@panwds/react-form'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../common/components/modal/Modal'
import {
  DATA_TEST_ADD_IDENTITY_ADDRESS,
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT
} from '../../../common/constants/TestingDataIds'
import SubscriptionService from '../../../subscription/services/SubscriptionService'
import ZIndex from '../../../common/components/zIndex/ZIndex'
import DropDown from '../../../subscription/components/dropdown/DropDown'
import '../../../iam/styles/Modals.scss'


const DELETE_CONFIRM_MESSAGE = 'This operation cannot be undone, so confirm this instance is not currently in use by any authentication profiles before deleting. Do you still want to delete this instance?'

const DELETE_REASONS_NOT_NEEDED = 'No longer needed'
const DELETE_REASON_SWITCH_DIFFERENT_ACCOUNT = 'Switching to different account'
const DELETE_REASON_CUSTOM = 'Custom'
const INSTNANCE_IN_USE_MSG = 'The instance cannot be deleted because it is being used by '
const NO_CSP_ACCESS_MSG = 'The instance cannot be deleted because you do not have access to the support account '

const deleteReasons = [
  { value: DELETE_REASONS_NOT_NEEDED },
  { value: DELETE_REASON_SWITCH_DIFFERENT_ACCOUNT },
  { value: DELETE_REASON_CUSTOM }
]

const DeleteInstanceModal = ({ closeModal, isOpen, instance, app, xsig, onDeleteInstance }) => {

  /**
  *     ========= Local State ===========
  */
  const [deleteReason, setDeleteReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (instance.cobUsage && instance.cobUsage.length > 0) {
      setError(`${INSTNANCE_IN_USE_MSG} ${instance.cobUsage[0].app_id}`)
    }
    if (!xsig) {
      setError(`${NO_CSP_ACCESS_MSG} ${instance.support_account_name}`)
    }
  }, [instance, setError, xsig])

  const onSubmit = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const params = {
        xsignature: xsig,
        reason: encodeURIComponent(customReason) || encodeURIComponent(deleteReason.value)
      }
      await SubscriptionService.instances.delete({ tsg_id: instance.tsg_id, instance_id: instance.tenant_id, params })
      onDeleteInstance(true, instance)
      closeModal()
    }
    catch (e) {
      setError(e.message || 'An error occured during activation.Please try again.')
      onDeleteInstance(false, instance, e.message || 'An error occured during activation.Please try again.')
      closeModal()
    }
    setIsLoading(false)
  }, [closeModal, customReason, deleteReason, instance, onDeleteInstance, xsig])

  const onReasonChange = useCallback((e) => {
    setDeleteReason(e.target.value)
  }, [])

  const title = `Delete ${app.display_name}`

  return (
    <ZIndex>
      <Modal style={{ top: '30%' }} className='delete_modal' size='md' onClose={closeModal} isOpen={isOpen}>
        <Form {...{ onSubmit }} mode='onBlur'>
          <div className='header-wrapper'>
            <ModalHeader {...{ title }} />
          </div>
          {isLoading ? (
            <ModalBody addClassName='tw-mr-4 tw-flex tw-flex-row tw-justify-center tw-items-center'>
              <LoadingIcon size="5xl" />
            </ModalBody>
          ) : (
            <ModalBody addClassName='tw-mr-4'>
              <div className='tw-mb-2'>
                {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
              </div>
              <div className='tw-mb-2'>
                <Bold appearance='semibold'>{DELETE_CONFIRM_MESSAGE}</Bold>
              </div>
              <FormLayout>
                <div className="tw-mt-2">
                  <DropDown
                    addClassName='cmm-modal-select'
                    itemList={deleteReasons}
                    label='Reason'
                    fieldName="delete_reason"
                    height={null}
                    width="100%"
                    placeholderValue={deleteReason ? null : 'Select Reason For Delete'}
                    selectProps={{
                      label: 'Reason',
                      showLabel: false,
                      disabled: false,
                      enableSearch: true,
                      fullWidth: true,
                      selectedItem: deleteReason,
                    }}
                    changeHandler={onReasonChange}
                  />
                  {deleteReason.value === DELETE_REASON_CUSTOM && <Input
                    data-testid={DATA_TEST_ADD_IDENTITY_ADDRESS}
                    label='Enter reason'
                    addClassName="tw-mt-1"
                    name="custom_reason"
                    placeholder='Custom reason'
                    required
                    onInput={(e) => setCustomReason(e.currentTarget.value)} />
                  }
                </div>
              </FormLayout>
            </ModalBody>
          )}
          <ModalFooter>
            <Button
              data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
              onClick={closeModal}
              appearance="secondary"
              disabled={isLoading}
            >Cancel</Button>
            <SubmitButton
              addClassName='dark:cm-text-red-100 cm-bg-red-500 dark:cm-bg-red-600 active:cm-bg-red-700 dark:active:cm-bg-red-400 focus-visible:cm-bg-red-600 dark:focus-visible:cm-bg-red-500 hover:cm-bg-red-600 dark:hover:cm-bg-red-500'
              data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
              disabled={isLoading || error || (!deleteReason && !customReason)}
            >Delete</SubmitButton>
          </ModalFooter>
        </Form>
      </Modal>
    </ZIndex>
  )
}
export default DeleteInstanceModal
